import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import './CategorySearchComboBox.css'

/**
 * Reusable Category Search Combo Box Component
 */
function CategorySearchComboBox({
  availableCategories = [],
  value = '',
  onChange,
  placeholder = 'Search or type category name...',
  id,
  label,
  disabled = false,
  size = 'medium',
  maxHeight = 200
}) {
  const [searchQuery, setSearchQuery] = useState(value || '')
  const [showDropdown, setShowDropdown] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })
  const searchInputRef = useRef(null)
  const dropdownRef = useRef(null)

  useEffect(() => {
    setSearchQuery(value || '')
  }, [value])

  const filteredCategories = availableCategories.filter(category =>
    category.toLowerCase().includes(searchQuery.toLowerCase())
  )

  function handleKeyDown(e) {
    if (disabled) return

    if (!showDropdown && filteredCategories.length > 0) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault()
        setShowDropdown(true)
        setHighlightedIndex(0)
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex(prev =>
          prev < filteredCategories.length - 1 ? prev + 1 : prev
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1)
        break
      case 'Enter':
        e.preventDefault()
        if (highlightedIndex >= 0 && filteredCategories[highlightedIndex]) {
          handleCategorySelect(filteredCategories[highlightedIndex])
        } else if (filteredCategories.length === 1) {
          handleCategorySelect(filteredCategories[0])
        }
        break
      case 'Escape':
        e.preventDefault()
        setShowDropdown(false)
        setHighlightedIndex(-1)
        break
      default:
        break
    }
  }

  function handleCategorySelect(category) {
    setSearchQuery(category)
    setShowDropdown(false)
    setHighlightedIndex(-1)
    if (onChange) {
      onChange(category)
    }
  }

  function handleSearchChange(e) {
    const query = e.target.value
    setSearchQuery(query)
    setShowDropdown(true)
    setHighlightedIndex(-1)

    const exactMatch = availableCategories.find(
      cat => cat.toLowerCase() === query.toLowerCase()
    )
    if (exactMatch) {
      if (onChange) {
        onChange(exactMatch)
      }
    } else if (!query) {
      if (onChange) {
        onChange('')
      }
    }
  }

  useEffect(() => {
    if (showDropdown && searchInputRef.current) {
      const updatePosition = () => {
        if (searchInputRef.current) {
          const rect = searchInputRef.current.getBoundingClientRect()
          setDropdownPosition({
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width
          })
        }
      }

      updatePosition()
      window.addEventListener('scroll', updatePosition, true)
      window.addEventListener('resize', updatePosition)

      return () => {
        window.removeEventListener('scroll', updatePosition, true)
        window.removeEventListener('resize', updatePosition)
      }
    }
  }, [showDropdown])

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target)
      ) {
        setShowDropdown(false)
      }
    }

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDropdown])

  const dropdownStyle = {
    top: `${dropdownPosition.top}px`,
    left: `${dropdownPosition.left}px`,
    width: `${Math.max(dropdownPosition.width, 260)}px`,
    minWidth: '260px',
    maxHeight: `${maxHeight}px`
  }

  return (
    <div className="category-combobox-root">
      {label && (
        <label htmlFor={id} className="category-combobox-label">
          {label}
        </label>
      )}
      <div className="category-combobox-input-wrap">
        <input
          ref={searchInputRef}
          id={id}
          type="text"
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          onFocus={() => !disabled && setShowDropdown(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="input category-combobox-input"
          data-size={size}
          aria-autocomplete="list"
          aria-expanded={showDropdown}
          aria-controls={id ? `${id}-dropdown` : 'category-dropdown'}
        />
        <span className="category-combobox-chevron">
          <i className="fas fa-search" aria-hidden="true"></i>
        </span>
      </div>

      {showDropdown && filteredCategories.length > 0 && createPortal(
        <div
          ref={dropdownRef}
          id={id ? `${id}-dropdown` : 'category-dropdown'}
          role="listbox"
          className="category-combobox-dropdown"
          style={dropdownStyle}
        >
          {filteredCategories.map((category, index) => (
            <div
              key={category}
              role="option"
              aria-selected={category === value}
              onClick={() => handleCategorySelect(category)}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={`category-combobox-option${index === highlightedIndex ? ' category-combobox-option--highlighted' : ''}${category === value ? ' category-combobox-option--selected' : ''}`}
            >
              {category}
              {category === value && (
                <span className="category-combobox-selected-mark"> ✓</span>
              )}
            </div>
          ))}
        </div>,
        document.body
      )}

      {showDropdown && searchQuery && filteredCategories.length === 0 && createPortal(
        <div className="category-combobox-dropdown category-combobox-empty" style={dropdownStyle}>
          No categories found matching "{searchQuery}"
        </div>,
        document.body
      )}

      {availableCategories.length > 0 && (
        <div className="category-combobox-count">
          {filteredCategories.length === availableCategories.length
            ? `${availableCategories.length} categories available`
            : `Showing ${filteredCategories.length} of ${availableCategories.length} categories`
          }
        </div>
      )}
    </div>
  )
}

export default CategorySearchComboBox
